/**
 * UX-GROWTH A17: sign people out after a period without activity, with a one-minute warning.
 * For shared warehouse and counter terminals. Opt-in per tenant: Settings → Security →
 * "Sign people out after inactivity" (security.idleSignOutMinutes; 0 / unset = off).
 *
 * Activity in any tab of the portal keeps every tab signed in (shared localStorage timestamp).
 * This is a client-side convenience on top of the server's session expiry, not a replacement.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export const LAST_ACTIVITY_KEY = 'bc-portal-last-activity';
export const IDLE_SIGNOUT_NOTICE_KEY = 'bc-portal-idle-signout';
export const WARNING_SECONDS = 60;
export const IDLE_CHOICES = [0, 15, 30, 60, 120, 240, 480] as const;
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const;
const TICK_MS = 1000;
const WRITE_THROTTLE_MS = 5000;

/** Valid minutes from a tenant setting, or 0 (off). */
export function idleMinutesFrom(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) && n >= 5 && n <= 24 * 60 ? Math.round(n) : 0;
}

const readLast = (): number => {
  try { const v = Number(localStorage.getItem(LAST_ACTIVITY_KEY)); return Number.isFinite(v) && v > 0 ? v : Date.now(); } catch { return Date.now(); }
};

export interface IdleSignOutProps {
  minutes: number;
  onSignOut: () => void;
  /** For tests. */
  now?: () => number;
}

const IdleSignOut: React.FC<IdleSignOutProps> = ({ minutes, onSignOut, now = Date.now }) => {
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  const lastWrite = useRef(0);
  const warning = secondsLeft !== null;
  // While the warning is up, ordinary clicks don't count; only "Stay signed in" does.
  const warningRef = useRef(false);
  warningRef.current = warning;

  const touch = useCallback((force = false) => {
    const t = now();
    if (!force && t - lastWrite.current < WRITE_THROTTLE_MS) { return; }
    lastWrite.current = t;
    try { localStorage.setItem(LAST_ACTIVITY_KEY, String(t)); } catch { /* storage off: this tab still tracks via lastWrite */ }
  }, [now]);

  useEffect(() => {
    if (!minutes) { return undefined; }
    touch(true);
    const onActivity = () => { if (!warningRef.current) { touch(); } };
    ACTIVITY_EVENTS.forEach(e => window.addEventListener(e, onActivity, { passive: true }));
    const id = window.setInterval(() => {
      const last = Math.max(readLast(), lastWrite.current);
      const left = Math.ceil((last + minutes * 60_000 - now()) / 1000);
      if (left <= 0) {
        try { sessionStorage.setItem(IDLE_SIGNOUT_NOTICE_KEY, String(minutes)); } catch { /* ignore */ }
        window.clearInterval(id);
        onSignOut();
        return;
      }
      setSecondsLeft(left <= WARNING_SECONDS ? left : null);
    }, TICK_MS);
    return () => { ACTIVITY_EVENTS.forEach(e => window.removeEventListener(e, onActivity)); window.clearInterval(id); };
  }, [minutes, onSignOut, now, touch]);

  if (!minutes) { return null; }
  return (
    <AlertDialog open={warning}>
      <AlertDialogContent data-testid="idle-warning">
        <AlertDialogHeader>
          <AlertDialogTitle>Are you still there?</AlertDialogTitle>
          <AlertDialogDescription>
            For security, you’ll be signed out in {secondsLeft ?? WARNING_SECONDS} second{secondsLeft === 1 ? '' : 's'} because there’s been no activity for a while.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => { try { sessionStorage.setItem(IDLE_SIGNOUT_NOTICE_KEY, 'manual'); } catch { /* ignore */ } onSignOut(); }}>Sign out now</AlertDialogCancel>
          <AlertDialogAction onClick={() => { touch(true); setSecondsLeft(null); }}>Stay signed in</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default IdleSignOut;

/** Login page: the message to show once after an idle sign-out (and clears it). */
export function takeIdleSignOutNotice(): string {
  try {
    const v = sessionStorage.getItem(IDLE_SIGNOUT_NOTICE_KEY);
    if (!v) { return ''; }
    sessionStorage.removeItem(IDLE_SIGNOUT_NOTICE_KEY);
    if (v === 'manual') { return 'You signed out.'; }
    return `You were signed out after ${v} minutes without activity. Sign in again to continue.`;
  } catch { return ''; }
}
